/**
 * ResourcePanel
 *
 * Left panel of the Warehouse Storage Manager. Shows all resource instances
 * that are currently available to be placed in a storage position.
 *
 * Data source: POST /endpoints/resourcesAvailableForStorage/pagination
 *   Each resource type section fetches its own page independently.
 *   Query params: excludeFixturesAssignedToResources=true&resourceType={type}
 *   Body: { page, size, columns, columnFilters, columnFilterValues, sortKey, sortMode }
 *
 * Features:
 *   - Text search filtering (server-side, debounced 300 ms)
 *   - Collapsible sections per resource type (Part open by default, others collapsed)
 *   - Server-side pagination within each section (PAGE_SIZE items per page)
 *   - Shows optional default fixture indicator per resource (resource.fixture.code)
 *   - Selected resource shown in a banner; click again or × to deselect
 *
 * Props:
 *   pendingResourceIds – Set<id> of resources already placed as pending (excluded client-side)
 *   refreshKey        – number; increment to force re-fetch after Save All
 *   selectedResource  – currently selected resource object or false
 *   searchText        – controlled search input value
 *   onSelectResource  – (resource | false) => void
 *   onSearchChange    – (text: string) => void
 */
import { useState, useEffect, useRef } from 'react'
import { Box, Typography, TextField, Stack, CircularProgress, IconButton, InputAdornment, Collapse, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { SearchRounded, CancelRounded, ExpandMoreRounded, ChevronLeftRounded, ChevronRightRounded, FirstPageRounded, LastPageRounded, Grid3x3Rounded } from '@mui/icons-material'
import { TYPE_META, TYPE_KEYS, dot, styles } from './warehouseStorageStyles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../generic/systemValues/systemValueLabel'
import { apiGet, apiPost } from '../generic/Api_Functions'
import { fabricationOrderName } from '../generic/utilities/fabricationOrderName'
import StatusFilterChips, { STATUS_COLORS } from './StatusFilterChips'
import FOFilterSection from './FOFilterSection'

const PAGE_SIZE = 7
const ENDPOINT = 'resourcesAvailableForStorage/pagination?excludeFixturesAssignedToResources=true'

function ResourceItem ({ resource, isSelected, onSelect }) {
  const { t_ } = useSmartTranslation()
  const typeColor = TYPE_META[resource.typeKey]?.color || '#9e9e9e'
  // Only parts have one; for everything else this is '' and nothing is rendered.
  const orderName = fabricationOrderName(resource)
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.2,
        py: 0.9,
        borderRadius: 1.5,
        cursor: 'pointer',
        minHeight: 52,
        bgcolor: isSelected ? alpha(typeColor, 0.1) : 'rgba(255,255,255,0.025)',
        border: '1px solid',
        borderColor: isSelected ? alpha(typeColor, 0.45) : 'rgba(255,255,255,0.05)',
        boxShadow: isSelected ? `0 0 10px ${alpha(typeColor, 0.2)}` : 'none',
        transition: 'all 0.15s ease',
        '&:hover': { bgcolor: alpha(typeColor, 0.07), borderColor: alpha(typeColor, 0.3) }
      }}
    >
      <Box sx={dot(10, typeColor)} />
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: isSelected ? typeColor : '#ccc', ...styles.truncated }}>
          {resource.name}
        </Typography>
        {resource.code && (
          <Typography sx={{ fontSize: '0.66rem', color: '#555' }}>{resource.code}</Typography>
        )}
        {orderName && (
          <Typography
            data-testid={`resource-fabrication-order-${resource.id}`}
            sx={{ fontSize: '0.62rem', color: '#8a8a8a', ...styles.truncated }}
          >
            {t_('Fabrication order')}: {orderName}
          </Typography>
        )}
        {resource.typeKey !== 'Fixture' && resource.fixture && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.3 }}>
            <Grid3x3Rounded sx={{ fontSize: 10, color: TYPE_META.Fixture.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.62rem', color: TYPE_META.Fixture.color, ...styles.truncated }}>
              {resource.fixture.code || resource.fixture.name}
            </Typography>
          </Box>
        )}
        {resource.status && (
          <Box sx={{
            display: 'inline-block',
            mt: 0.3,
            px: 0.5,
            py: 0.1,
            borderRadius: 0.6,
            bgcolor: alpha(STATUS_COLORS[resource.status] || '#9e9e9e', 0.15),
            border: `1px solid ${alpha(STATUS_COLORS[resource.status] || '#9e9e9e', 0.4)}`
          }}
          >
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: STATUS_COLORS[resource.status] || '#9e9e9e', letterSpacing: '0.03em' }}>
              {systemValueCodeLabel(t_, 'PartStatusEnum', resource.status)}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{
        fontSize: '0.58rem',
        color: typeColor,
        bgcolor: alpha(typeColor, 0.1),
        px: 0.6,
        py: 0.2,
        borderRadius: 0.8,
        flexShrink: 0,
        fontWeight: 700,
        letterSpacing: '0.04em'
      }}
      >
        {resource.typeKey === 'RawMaterial' ? 'RM' : resource.typeKey[0]}
      </Box>
    </Box>
  )
}

function ResourceSection ({ typeKey, searchText, pendingResourceIds, selectedResource, onSelectResource, isOpen, onToggle, refreshKey, onFixturesLoaded }) {
  const { t_ } = useSmartTranslation()
  const typeColor = TYPE_META[typeKey]?.color || '#9e9e9e'
  const label = t_(TYPE_META[typeKey].label)

  const [items, setItems] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const debounceRef = useRef(null)

  // Part-only filter state — managed internally so filters belong to this section
  const [selectedFOs, setSelectedFOs] = useState([])
  const [fabricationOrders, setFabricationOrders] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [partStatuses, setPartStatuses] = useState([])

  useEffect(() => {
    if (typeKey !== 'Part') return
    apiGet('partStatuses')
      .then(data => setPartStatuses(Array.isArray(data) ? data.map(s => ({ id: s, name: s })) : []))
      .catch(() => {})
    apiGet('getAllUnfinishedAndActiveFabricationOrders')
      .then(data => {
        setFabricationOrders((Array.isArray(data) ? data : []).map(fo => ({
          id: fo.fabricationOrderId,
          name: fo.fabricationOrderName
        })))
      })
      .catch(() => {})
  }, [typeKey])

  const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)

  // For Part sections: build query params with fabrication order filter
  const buildUrl = () => {
    let url = ENDPOINT + '&resourceType=' + typeKey
    if (typeKey === 'Part' && selectedFOs && selectedFOs.length > 0) {
      selectedFOs.forEach(fo => { url += '&fabricationOrderIds=' + fo.id })
    }
    if (typeKey === 'Part' && selectedStatuses && selectedStatuses.length > 0) {
      selectedStatuses.forEach(s => { url += '&statusFilter=' + (s.id || s) })
    }
    return url
  }

  // Parts always sort by nextPlanDate ASC so most-urgent appear first.
  // FO filter is independent — applied only when selectedFOs is non-empty.
  const isPart = typeKey === 'Part'

  // Fetch data — triggered by page, debounced search, refreshKey, or selectedFOs
  //
  // ⚠️ The window asked for is NOT one page: it is every page up to the current one PLUS as many
  // extra rows as there are pending placements. The reason is that the server knows nothing about
  // pending placements -- they are not saved yet -- so the ones already placed come back in the
  // answer and are dropped here. Asking for exactly PAGE_SIZE meant the list SHRANK with every
  // placement (7, 6, 5...) and only refilled when the user changed page, which is exactly the
  // symptom reported from the plant: "with 40 to place you put 7 in, change page, put 7 in".
  // Over-fetching by the number of pending ids guarantees the page can always be filled while the
  // server still has rows, and costs one small request.
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setLoading(true)
      const windowSize = (safePage + 1) * PAGE_SIZE + pendingResourceIds.size
      apiPost(
        buildUrl(),
        {
          page: 0,
          size: windowSize,
          columns: isPart ? ['name', 'code', 'type.name', 'nextPlanDate'] : ['name', 'code', 'type.name'],
          columnFilters: searchText ? ['name'] : [],
          columnFilterValues: searchText ? [searchText.toLowerCase()] : [],
          sortKey: isPart ? ['nextPlanDate'] : ['name'],
          sortMode: [1],
          rows: [],
          primaryKeys: ['id']
        }
      )
        .then(data => {
          const content = (data.content || []).map(r => ({ ...r, typeKey: (r.type?.name || typeKey).replace(/\s+/g, '') }))
          const placeable = content.filter(r => !pendingResourceIds.has(r.id))
          setItems(placeable.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE))
          // The rows dropped inside the window are gone from the count too, or the pager would offer
          // a page that no longer has anything to show.
          const hiddenInWindow = content.length - placeable.length
          setTotalElements(Math.max(0, (data.totalElements || 0) - hiddenInWindow))
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false))
    }, searchText ? 300 : 0)

    return () => clearTimeout(debounceRef.current)
  }, [typeKey, safePage, searchText, pendingResourceIds, refreshKey, selectedFOs, selectedStatuses]) // eslint-disable-line

  // Reset page when search, FO filter, or status filter changes
  useEffect(() => {
    setPage(0)
  }, [searchText, selectedFOs, selectedStatuses])

  // When this section is a Fixture section and the parent wants all fixtures for the dialog,
  // make a separate non-paginated call (size 50) and propagate the result upward.
  useEffect(() => {
    if (typeKey !== 'Fixture' || !onFixturesLoaded) return
    apiPost(ENDPOINT + '&resourceType=Fixture', {
      page: 0,
      size: 50,
      columns: ['name', 'code', 'type.name'],
      columnFilters: [],
      columnFilterValues: [],
      sortKey: ['name'],
      sortMode: [1],
      rows: [],
      primaryKeys: ['id']
    })
      .then(data => {
        const all = (data.content || []).map(r => ({ ...r, typeKey: 'Fixture' }))
        onFixturesLoaded(all)
      })
      .catch(() => onFixturesLoaded([]))
  }, [typeKey, onFixturesLoaded, refreshKey]) // eslint-disable-line

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.75,
          cursor: 'pointer',
          borderRadius: 1,
          minHeight: 44,
          borderLeft: `3px solid ${typeColor}`,
          bgcolor: isOpen ? alpha(typeColor, 0.07) : 'transparent',
          '&:hover': { bgcolor: alpha(typeColor, 0.05) },
          transition: 'background 0.15s',
          userSelect: 'none'
        }}
      >
        <Box sx={dot(8, typeColor)} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: typeColor, flexGrow: 1 }}>
          {label}
        </Typography>
        {loading
          ? <CircularProgress size={12} sx={{ color: typeColor, mr: 0.5 }} />
          : <Typography sx={{ fontSize: '0.68rem', color: '#555', mr: 0.5 }}>{totalElements}</Typography>}
        <ExpandMoreRounded sx={{
          fontSize: 18,
          color: '#555',
          transform: isOpen ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s'
        }}
        />
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, pl: 0.5, pt: 0.4, pb: 0.5 }}>
          {/* Part-only filters — shown inside the section */}
          {typeKey === 'Part' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, px: 0.5, pb: 0.4 }}>
              <FOFilterSection orders={fabricationOrders} selected={selectedFOs} onChange={setSelectedFOs} />
              <StatusFilterChips statuses={partStatuses} selected={selectedStatuses} onChange={setSelectedStatuses} />
            </Box>
          )}
          {loading && items.length === 0
            ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}><CircularProgress size={20} sx={{ color: typeColor }} /></Box>
            : items.length === 0
              ? <Typography sx={{ fontSize: '0.72rem', color: '#444', px: 1, py: 0.5 }}>—</Typography>
              : items.map(resource => {
                const isSelected = selectedResource?.id === resource.id && selectedResource?.typeKey === resource.typeKey
                return (
                  <ResourceItem
                    key={`${resource.typeKey}-${resource.id}`}
                    resource={resource}
                    isSelected={isSelected}
                    onSelect={() => onSelectResource(isSelected ? null : resource)}
                  />
                )
              })}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5, px: 0.5 }}>
              <Stack direction='row' spacing={0}>
                <IconButton
                  size='small'
                  disabled={safePage === 0}
                  onClick={e => { e.stopPropagation(); setPage(0) }}
                  sx={{ color: typeColor, p: 0.5, minWidth: 32, minHeight: 36, opacity: safePage === 0 ? 0.3 : 1 }}
                >
                  <FirstPageRounded sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={safePage === 0}
                  onClick={e => { e.stopPropagation(); setPage(safePage - 1) }}
                  sx={{ color: typeColor, p: 0.5, minWidth: 32, minHeight: 36, opacity: safePage === 0 ? 0.3 : 1 }}
                >
                  <ChevronLeftRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>{safePage + 1} / {totalPages}</Typography>
              <Stack direction='row' spacing={0}>
                <IconButton
                  size='small'
                  disabled={safePage >= totalPages - 1}
                  onClick={e => { e.stopPropagation(); setPage(safePage + 1) }}
                  sx={{ color: typeColor, p: 0.5, minWidth: 32, minHeight: 36, opacity: safePage >= totalPages - 1 ? 0.3 : 1 }}
                >
                  <ChevronRightRounded sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={safePage >= totalPages - 1}
                  onClick={e => { e.stopPropagation(); setPage(totalPages - 1) }}
                  sx={{ color: typeColor, p: 0.5, minWidth: 32, minHeight: 36, opacity: safePage >= totalPages - 1 ? 0.3 : 1 }}
                >
                  <LastPageRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default function ResourcePanel ({ pendingResourceIds, refreshKey, selectedResource, searchText, onSelectResource, onSearchChange, typeFilter, onFixturesLoaded }) {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const [openSections, setOpenSections] = useState(new Set(['Part']))

  const toggleSection = (typeKey) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(typeKey)) next.delete(typeKey)
      else next.add(typeKey)
      return next
    })
  }

  return (
    <Box sx={{ width: 272, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, overflow: 'hidden', ...styles.panelCard }}>
      <Typography sx={{ ...styles.sectionTitle, color: accent }}>{t_('Resources')}</Typography>

      <TextField
        placeholder={t_('Search resources...')}
        size='small'
        value={searchText}
        onChange={e => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchRounded sx={{ color: '#555', fontSize: 20 }} />
            </InputAdornment>
          )
        }}
        sx={{
          ...styles.textField,
          '& .MuiOutlinedInput-root': {
            ...styles.textField['& .MuiOutlinedInput-root'],
            '&:hover fieldset': { borderColor: alpha(accent, 0.35) },
            '&.Mui-focused fieldset': { borderColor: accent }
          }
        }}
      />

      {selectedResource && (
        <Box sx={styles.selectedResourceBanner}>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ overflow: 'hidden' }}>
            <Box sx={dot(8, TYPE_META[selectedResource.typeKey]?.color || '#9e9e9e')} />
            <Typography sx={{ fontSize: '0.75rem', color: accent, fontWeight: 500, ...styles.truncated }}>
              {selectedResource.name}
            </Typography>
          </Stack>
          <IconButton size='small' onClick={() => onSelectResource(null)} sx={{ color: accent, p: 0.3, flexShrink: 0 }}>
            <CancelRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {(typeFilter ? [typeFilter] : TYPE_KEYS).map(typeKey => (
          <ResourceSection
            key={typeKey}
            typeKey={typeKey}
            searchText={searchText}
            pendingResourceIds={pendingResourceIds}
            selectedResource={selectedResource}
            onSelectResource={onSelectResource}
            isOpen={openSections.has(typeKey)}
            onToggle={() => toggleSection(typeKey)}
            refreshKey={refreshKey}
            onFixturesLoaded={typeKey === 'Fixture' ? onFixturesLoaded : undefined}
          />
        ))}
      </Box>
    </Box>
  )
}
